import { Link } from '@tanstack/react-router';
import { ExternalLink, type LucideIcon } from 'lucide-react';
import type { TaskResponse } from '@vagusx/clients';
import { Skeleton } from '@/components/ui/skeleton';

interface TaskListSectionProps {
  title: string;
  icon: LucideIcon;
  tasks: TaskResponse[] | undefined;
  isLoading: boolean;
  /** Per-row badge cluster — caller composes priority / status badges. */
  renderBadges: (task: TaskResponse) => React.ReactNode;
}

/**
 * Shared empty-aware section renderer used by ChildTasksList and
 * RelatedPatientTasks. Hides itself when there are no rows so the
 * Overview tab doesn't grow chrome for tasks with no relations.
 */
export function TaskListSection({
  title,
  icon: Icon,
  tasks,
  isLoading,
  renderBadges,
}: TaskListSectionProps): React.ReactElement | null {
  if (isLoading) {
    return (
      <section className="rounded-lg border bg-card p-4">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="mt-3 h-10 w-full" />
      </section>
    );
  }
  if (!tasks || tasks.length === 0) return null;

  return (
    <section className="rounded-lg border bg-card p-4">
      <div className="mb-3 flex items-center gap-2">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold">{title}</h3>
        <span className="text-xs text-muted-foreground">· {tasks.length}</span>
      </div>
      <ul className="space-y-1.5">
        {tasks.map((task) => (
          <TaskRow key={task.id} task={task} badges={renderBadges(task)} />
        ))}
      </ul>
    </section>
  );
}

function TaskRow({
  task,
  badges,
}: {
  task: TaskResponse;
  badges: React.ReactNode;
}): React.ReactElement {
  return (
    <li>
      <Link
        to="/tasks/$taskId"
        params={{ taskId: task.id }}
        className="group flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent"
      >
        {badges}
        <span className="min-w-0 flex-1 truncate">{task.title}</span>
        <ExternalLink className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100" />
      </Link>
    </li>
  );
}
